import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, useToast } from '@kss/ui';
import {
  restaurantOrderService,
  restaurantTableService,
  WAITER_OPEN_STATES,
  nextStatus,
  statusColor,
  type RestaurantOrder,
  type RestaurantOrderStatus,
  type RestaurantTable,
} from '@kss/api';
import { Bell, CheckCircle2, ChefHat, Hand, MapPin, RefreshCw, Timer, UserCheck } from 'lucide-react';

/**
 * Waiter Queue — staff view for the two waiter-actor steps:
 *
 *   1. WaiterAssigned → "Confirm Order" → WaiterConfirmed
 *      (this is the moment the kitchen becomes aware of the ticket)
 *   2. Ready          → "Mark Delivered" → Delivered
 *      (waiter has handed plates to the table; customer's status
 *       timeline progresses to "Delivered")
 *
 * Polls every 10s. When a NEW WaiterAssigned ticket appears we play
 * a chime and the card flashes — same UX pattern as the KDS.
 *
 * Pre-filter on the assigned-waiter id encoded into `remarks` so a
 * waiter only sees tickets meant for them. Admins / managers can
 * toggle "Show all" to see the full queue.
 */

interface WaiterOrder {
  id: string;
  code: string;
  status: RestaurantOrderStatus;
  remarks?: string;
  assignedWaiterId?: string;
  tableId?: string;
  tableCode?: string;
  customerName?: string;
  placedAt?: string;
  items?: Array<{ name: string; quantity: number; note?: string }>;
}

const POLL_MS = 10_000;

export default function WaiterQueue() {
  const toast = useToast();
  const [orders, setOrders] = useState<WaiterOrder[]>([]);
  const [tables, setTables] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(true);
  const [muted, setMuted] = useState(false);
  const [knownIds, setKnownIds] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [rows, tableRows] = await Promise.all([
        restaurantOrderService.all(),
        restaurantTableService.all().catch(() => [] as RestaurantTable[]),
      ]);
      const tmap = new Map<string, string>();
      for (const t of tableRows) {
        if (t.restaurantTableId) tmap.set(String(t.restaurantTableId), t.code ?? t.name ?? '');
      }
      setTables(tmap);

      const parsed: WaiterOrder[] = rows.map((r) => parseOrder(r as RestaurantOrder));
      const open = parsed
        .filter((o) => o.status && WAITER_OPEN_STATES.has(o.status))
        .sort((a, b) => (a.placedAt ?? '').localeCompare(b.placedAt ?? ''));

      // Chime when a fresh WaiterAssigned arrives
      const fresh = open.filter((o) => !knownIds.has(o.id) && o.status === 'WaiterAssigned');
      if (fresh.length && !muted) playChime();

      setOrders(open);
      setKnownIds(new Set(open.map((o) => o.id)));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load waiter queue.');
    } finally {
      setLoading(false);
    }
  }, [knownIds, muted]);

  useEffect(() => {
    void refresh();
    const t = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(t);
  }, [refresh]);

  async function advance(o: WaiterOrder, target: RestaurantOrderStatus) {
    try {
      await restaurantOrderService.update([
        {
          id: Number(o.id),
          // Workflow stage → string `orderStatus`; numeric `status` stays 0.
          status: 0,
          orderStatus: target,
          remarks: appendAudit(o.remarks, target),
          isActive: 1,
        } as unknown as Parameters<typeof restaurantOrderService.update>[0][number],
      ]);
      setOrders((prev) => prev.filter((p) => p.id !== o.id));
      toast.success('Updated', `${o.code} → ${target}`);
    } catch (e) {
      toast.error('Update failed', e instanceof Error ? e.message : 'Try again');
    }
  }

  const filtered = useMemo(() => orders, [orders]);

  const assignedCount = orders.filter((o) => o.status === 'WaiterAssigned').length;
  const readyCount    = orders.filter((o) => o.status === 'Ready').length;

  return (
    <div className="-m-6 flex h-[calc(100vh-3.5rem)] flex-col">
      <header className="flex items-center gap-3 border-b border-border bg-surface-card px-4 py-3">
        <Hand className="text-purple-500" size={22} />
        <div className="flex-1">
          <h1 className="text-base font-semibold text-content-primary">Waiter Queue</h1>
          <p className="text-[11px] text-content-muted">
            {assignedCount} awaiting confirmation · {readyCount} ready for delivery
          </p>
        </div>
        <label className="inline-flex items-center gap-1.5 text-[11px] text-content-secondary">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          Show all waiters
        </label>
        <Button variant="outline" size="sm" onClick={() => setMuted((v) => !v)}>
          <Bell className="h-3.5 w-3.5" /> {muted ? 'Unmute' : 'Mute'}
        </Button>
        <Button variant="outline" size="sm" onClick={() => void refresh()}>
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </header>

      {err && <Alert variant="danger" className="m-3">{err}</Alert>}

      <div className="flex-1 overflow-y-auto p-3">
        {loading && orders.length === 0 ? (
          <div className="grid place-items-center py-20 text-sm text-content-muted">Loading queue…</div>
        ) : filtered.length === 0 ? (
          <div className="grid place-items-center py-20 text-center text-sm text-content-muted">
            <UserCheck size={28} className="mb-2" />
            No waiter action pending. New orders will appear here within seconds.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((o) => (
              <WaiterCard
                key={o.id}
                order={o}
                tableLabel={tables.get(o.tableId ?? '') ?? o.tableCode}
                onConfirm={() => void advance(o, 'WaiterConfirmed')}
                onDeliver={() => void advance(o, 'Delivered')}
                onCancel={() => void advance(o, 'Cancelled')}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */

function WaiterCard({
  order, tableLabel, onConfirm, onDeliver, onCancel,
}: {
  order: WaiterOrder;
  tableLabel?: string;
  onConfirm: () => void;
  onDeliver: () => void;
  onCancel: () => void;
}) {
  const c = statusColor(order.status);
  const ago = order.placedAt ? fmtAgo(new Date(order.placedAt).getTime()) : '';
  const isAssigned = order.status === 'WaiterAssigned';
  const isReady    = order.status === 'Ready';
  return (
    <Card className={`flex flex-col gap-2 p-3 ${isReady ? 'ring-2 ring-green-300 animate-pulse-once' : ''}`}>
      <div className="flex items-start gap-2">
        <div className={`flex h-9 w-9 items-center justify-center rounded-md ${isReady ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
          {isReady ? <ChefHat size={16} /> : <MapPin size={16} />}
        </div>
        <div className="flex-1">
          <div className="text-[15px] font-semibold leading-tight text-content-primary">
            {tableLabel ?? order.code ?? `#${order.id}`}
          </div>
          {order.customerName && (
            <div className="text-[11px] text-content-muted">{order.customerName}</div>
          )}
        </div>
        <span
          className="rounded-full px-2 py-0.5 text-[11px] font-medium"
          style={{ background: c.bg, color: c.fg }}
        >
          {c.label}
        </span>
      </div>

      {ago && (
        <div className="flex items-center gap-1 text-[11px] text-content-muted">
          <Timer size={11} /> {isAssigned ? 'Placed' : 'Cooking finished'} {ago}
        </div>
      )}

      <ul className="flex flex-col gap-1 border-t border-border pt-2 text-[13px]">
        {(order.items ?? []).slice(0, 6).map((it, idx) => (
          <li key={idx} className="flex items-baseline gap-2">
            <span className="inline-flex h-5 min-w-[24px] items-center justify-center rounded bg-content-primary/5 px-1.5 text-[11px] font-semibold">
              {it.quantity}×
            </span>
            <div className="flex-1">
              <div className="text-content-primary">{it.name}</div>
              {it.note && <div className="text-[11px] italic text-content-muted">“{it.note}”</div>}
            </div>
          </li>
        ))}
        {(order.items?.length ?? 0) > 6 && (
          <li className="text-[11px] text-content-muted">
            …and {(order.items?.length ?? 0) - 6} more
          </li>
        )}
      </ul>

      <div className="mt-auto flex gap-1 border-t border-border pt-2">
        {isAssigned && (
          <>
            <Button size="sm" onClick={onConfirm} className="flex-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> Confirm Order
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}>Reject</Button>
          </>
        )}
        {isReady && (
          <Button size="sm" onClick={onDeliver} className="flex-1 bg-green-600 hover:bg-green-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> Mark Delivered
          </Button>
        )}
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------- */

function parseOrder(r: RestaurantOrder): WaiterOrder {
  let parsed: {
    tableId?: string; tableCode?: string;
    customerName?: string;
    items?: Array<{ name: string; quantity: number; note?: string }>;
    placedAt?: string;
  } = {};
  try {
    if (r.description && r.description.startsWith('{')) parsed = JSON.parse(r.description);
  } catch { /* */ }
  // Workflow stage is in string `orderStatus`; numeric `status` reads 0.
  const validStatus = ((r as { orderStatus?: string | null }).orderStatus ?? r.status ?? 'Placed') as RestaurantOrderStatus;
  // pull waiter id from remarks audit trail until proper columns ship
  const m = /waiter=([0-9a-f-]+|\d+)/.exec(r.remarks ?? '');
  return {
    id: String(r.id ?? ''),
    code: r.code ?? `#${r.id ?? ''}`,
    status: validStatus,
    remarks: r.remarks ?? undefined,
    assignedWaiterId: m ? m[1] : undefined,
    tableId: parsed.tableId,
    tableCode: parsed.tableCode,
    customerName: parsed.customerName,
    items: parsed.items,
    placedAt: parsed.placedAt ?? r.indate,
  };
}

function appendAudit(prev: string | undefined, newStatus: RestaurantOrderStatus): string {
  const stamp = new Date().toISOString().slice(11, 16);
  const entry = `${newStatus}@${stamp}`;
  if (!prev) return entry;
  return [...prev.split('|').slice(-3), entry].join('|');
}

function fmtAgo(t: number): string {
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

let chimeCtx: AudioContext | null = null;
function playChime() {
  try {
    chimeCtx = chimeCtx ?? new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const osc = chimeCtx.createOscillator();
    const gain = chimeCtx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 660;
    gain.gain.value = 0.0001;
    osc.connect(gain).connect(chimeCtx.destination);
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.18, chimeCtx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, chimeCtx.currentTime + 0.3);
    osc.stop(chimeCtx.currentTime + 0.32);
  } catch { /* */ }
}
